import React, { useEffect, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet, matchRoutes, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { getMeThunk } from './store';
import { ThemeProvider } from './context/ThemeContext';
import ErrorBoundary, { RouteErrorFallback } from './components/common/ErrorBoundary';
import lazyWithRetry from './utils/lazyWithRetry';
import { captureAttribution } from './utils/attribution';
import { useI18n } from './context/I18nContext';

import Layout from './components/common/Layout';
import CookieConsent from './components/common/CookieConsent';
import PaywallHost from './components/common/PaywallHost';
import RouteMeta from './components/common/RouteMeta';
import ClientOnly from './components/common/ClientOnly';
import PrivateRoute from './components/common/PrivateRoute';
import AdminRoute from './components/common/AdminRoute';

// Use lazyWithRetry so a stale browser tab (cached old chunk hashes after a
// new deploy) auto-reloads once instead of getting stuck on ChunkLoadError.
const HomePage = lazyWithRetry(() => import('./pages/HomePage'));
const LoginPage = lazyWithRetry(() => import('./pages/LoginPage'));
const RegisterPage = lazyWithRetry(() => import('./pages/RegisterPage'));
const PrivacyPage = lazyWithRetry(() => import('./pages/PrivacyPage'));
const TermsPage = lazyWithRetry(() => import('./pages/TermsPage'));
const RefundsPage = lazyWithRetry(() => import('./pages/RefundsPage'));
const ForgotPasswordPage = lazyWithRetry(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazyWithRetry(() => import('./pages/ResetPasswordPage'));
const DashboardPage = lazyWithRetry(() => import('./pages/DashboardPage'));
const WeddingDetailPage = lazyWithRetry(() => import('./pages/WeddingDetailPage'));
const CreateWeddingPage = lazyWithRetry(() => import('./pages/CreateWeddingPage'));
const RsvpDashboardPage = lazyWithRetry(() => import('./pages/RsvpDashboardPage'));
const GalleryPage = lazyWithRetry(() => import('./pages/GalleryPage'));
const GalleryManagePage = lazyWithRetry(() => import('./pages/GalleryManagePage'));
const CreateGalleryPage = lazyWithRetry(() => import('./pages/CreateGalleryPage'));
const ClientGalleryPage = lazyWithRetry(() => import('./pages/ClientGalleryPage'));
const SeatingChartPage = lazyWithRetry(() => import('./pages/SeatingChartPage'));
const PublicRsvpPage = lazyWithRetry(() => import('./pages/PublicRsvpPage'));
const PricingPage = lazyWithRetry(() => import('./pages/PricingPage'));
const VerifyEmailPage = lazyWithRetry(() => import('./pages/VerifyEmailPage'));
const PaymentSuccessPage = lazyWithRetry(() => import('./pages/PaymentSuccessPage'));
const PaymentCancelPage = lazyWithRetry(() => import('./pages/PaymentCancelPage'));
const AdminDashboardPage = lazyWithRetry(() => import('./pages/AdminDashboardPage'));
const GuestMomentUploadPage = lazyWithRetry(() => import('./pages/GuestMomentUploadPage'));
const GuestMomentsLivePage = lazyWithRetry(() => import('./pages/GuestMomentsLivePage'));
const FindSeatPage = lazyWithRetry(() => import('./pages/FindSeatPage'));
const NotFoundPage = lazyWithRetry(() => import('./pages/NotFoundPage'));

function PageLoader() {
  const { t } = useI18n();
  return (
    <div className="flex items-center justify-center h-screen bg-stone-50 dark:bg-[#13131a] text-stone-400 dark:text-stone-500">
      {t('common.loading') || 'Loading…'}
    </div>
  );
}

function RootLayout() {
  const { pathname, search } = useLocation();

  // First-touch ?ref= / utm_* capture (skipped on guest pages; sent at signup).
  useEffect(() => {
    captureAttribution({ pathname, search });
  }, [pathname, search]);

  return (
    <>
      <RouteMeta />
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        theme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
      />
      <Suspense fallback={<PageLoader />}>
        <Outlet />
      </Suspense>
      <ClientOnly>
        <CookieConsent />
        <PaywallHost />
      </ClientOnly>
    </>
  );
}

const routes = [
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <RouteErrorFallback />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'auth/login', element: <LoginPage /> },
      { path: 'auth/register', element: <RegisterPage /> },
      { path: 'auth/forgot-password', element: <ForgotPasswordPage /> },
      { path: 'auth/reset-password/:token', element: <ResetPasswordPage /> },
      { path: 'auth/verify-email/:token', element: <VerifyEmailPage /> },
      { path: 'rsvp/:slug', element: <PublicRsvpPage /> },
      { path: 'invite/:slug', element: <PublicRsvpPage /> },
      { path: 'rsvp/edit/:token', element: <PublicRsvpPage editMode /> },
      { path: 'gallery/:shareToken', element: <ClientGalleryPage /> },
      { path: 'moments/:uploadToken', element: <GuestMomentUploadPage /> },
      { path: 'moments/:uploadToken/live', element: <GuestMomentsLivePage /> },
      { path: 'find-seat/:slug', element: <FindSeatPage /> },
      // Public demo links for social bios (no signup, read-only).
      { path: 'demo', element: <PublicRsvpPage demo /> },
      { path: 'demo/live', element: <GuestMomentsLivePage demo /> },
      { path: 'pricing', element: <PricingPage /> },
      { path: 'privacy', element: <PrivacyPage /> },
      { path: 'terms', element: <TermsPage /> },
      { path: 'refunds', element: <RefundsPage /> },
      {
        element: <PrivateRoute />,
        children: [
          {
            element: <Layout />,
            children: [
              { path: 'dashboard', element: <DashboardPage /> },
              { path: 'weddings/new', element: <CreateWeddingPage /> },
              { path: 'weddings/:id', element: <WeddingDetailPage /> },
              { path: 'weddings/:id/rsvp', element: <RsvpDashboardPage /> },
              { path: 'weddings/:id/gallery', element: <GalleryPage /> },
              { path: 'galleries/new', element: <CreateGalleryPage /> },
              { path: 'galleries/:id', element: <GalleryManagePage /> },
              { path: 'weddings/:id/seating', element: <SeatingChartPage /> },
              { path: 'payment/success', element: <PaymentSuccessPage /> },
              { path: 'payment/cancel', element: <PaymentCancelPage /> },
            ],
          },
        ],
      },
      {
        element: <AdminRoute />,
        children: [
          {
            element: <Layout />,
            children: [{ path: 'admin/:weddingId', element: <AdminDashboardPage /> }],
          },
        ],
      },
      // Billing emails link to /dashboard/billing, which has no page of its own.
      { path: 'dashboard/*', element: <Navigate to="/dashboard" replace /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];

const router = createBrowserRouter(routes);

/** Loads the code of every lazy page `pathname` renders, so the first render doesn't suspend. */
export function preloadRoute(pathname) {
  const matches = matchRoutes(routes, pathname) || [];
  return Promise.all(matches.map((m) => m.route.element?.type?.preload?.()).filter(Boolean));
}

export default function App() {
  const dispatch = useDispatch();
  const { token, initialized } = useSelector((s) => s.auth);

  useEffect(() => {
    if (token) dispatch(getMeThunk());
    // eslint-disable-next-line
  }, []);

  if (token && !initialized) {
    return <PageLoader />;
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <RouterProvider router={router} />
      </ThemeProvider>
    </ErrorBoundary>
  );
}
